/**
 * Shared aggregation utility functions.
 *
 * These are used by both engine-summary components and full-audit aggregation logic.
 */

/**
 * Parse an install impact string like "+400-750 installs/month" or "+10000 installs/month" into {low, high}.
 */
export function parseInstallImpactRange(impact: string): { low: number; high: number } | null {
  // Try range format first: "+400-750 installs/month"
  const rangeMatch = impact.match(/\+?([\d,]+)\s*-\s*([\d,]+)/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1].replace(/,/g, ""), 10);
    const high = parseInt(rangeMatch[2].replace(/,/g, ""), 10);
    if (Number.isNaN(low) || Number.isNaN(high)) return null;
    return { low, high };
  }
  // Try single value format: "+10000 installs/month"
  const singleMatch = impact.match(/\+?([\d,]+)\s+installs/);
  if (singleMatch) {
    const value = parseInt(singleMatch[1].replace(/,/g, ""), 10);
    if (Number.isNaN(value)) return null;
    return { low: value, high: value };
  }
  return null;
}

/**
 * Parse a benchmark % lift string like "+10-20% installs" into {low, high}.
 */
export function parsePctLiftRange(pctLift: string): { low: number; high: number } | null {
  const rangeMatch = pctLift.match(/\+?([\d.]+)\s*-\s*([\d.]+)%/);
  if (rangeMatch) {
    const low = parseFloat(rangeMatch[1]);
    const high = parseFloat(rangeMatch[2]);
    if (Number.isNaN(low) || Number.isNaN(high)) return null;
    return { low, high };
  }
  const singleMatch = pctLift.match(/\+?([\d.]+)%/);
  if (singleMatch) {
    const value = parseFloat(singleMatch[1]);
    if (Number.isNaN(value)) return null;
    return { low: value, high: value };
  }
  return null;
}

/**
 * Format a number with commas for display.
 */
export function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}
